// 10 · gráfico "Edge + línea de la casa de apuestas" — SVG propio con la
// geometría del prototipo (viewBox 330×178, plot x 34–296, y 12–150, doble
// eje: izq. línea O/U oro #B98A12, der. edge gris). Datos: el MISMO
// pick.edge_history [{t, e, l}] que dibuja DetailPanel.EdgeChart del
// dashboard (t = minuto absoluto de juego, e = edge, l = línea O/U).
//
// Umbral real por deporte (Matías): básquet ±10 · handball +3 (OVER) / −2
// (UNDER), dash 4 4 #5FB3A1 con etiqueta del valor real. Handball: eje X en
// mitades 0–60 con marca HT. Área verde sobre 0 (OVER), azul bajo 0 (UNDER).
import { useMemo } from 'react';

const X0 = 34, X1 = 296, Y0 = 12, Y1 = 150, YC = 81, HALF = 69;
const GOLD = '#B98A12', UNDER = '#4F6BFF', OVER = '#16A34A', UMB = '#5FB3A1';

function gameDurationFromLeague(liga) {
  const ln = String(liga || '').toLowerCase();
  if (ln.includes('wnba') || ln.includes('nba w') || /\bnba\s*w(omen)?\b/.test(ln)) return 40;
  if (ln.includes('nba') || ln.includes('g league') || ln.includes('g-league')) return 48;
  return 40;
}

// Catmull-Rom → Bézier cúbica (curva suave por los puntos)
function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)},${c2x.toFixed(1)} ${c2y.toFixed(1)},${p2.x} ${p2.y}`;
  }
  return d;
}
const f1 = (n) => (Math.round(n * 10) / 10).toFixed(1);
const fs = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n)}`;

export function useChartModel(pick, esHb) {
  return useMemo(() => {
    const hist = (Array.isArray(pick?.edge_history) ? pick.edge_history : [])
      .filter((p) => p && typeof p.e === 'number' && typeof p.t === 'number')
      .sort((a, b) => a.t - b.t);
    if (hist.length < 2) return null;
    const dur = esHb ? 60 : (Number(pick?.duracion) || gameDurationFromLeague(pick?.liga));
    const qLen = dur / 4;
    const umbP = esHb ? 3 : 10, umbN = esHb ? -2 : -10;
    // dominio del edge: ±20 (básquet) / ±5 (handball); crece si la serie se sale
    const maxAbs = Math.max(esHb ? 5 : 20, ...hist.map((p) => Math.abs(p.e)));
    const DMAX = esHb ? Math.ceil(maxAbs) : Math.ceil(maxAbs / 5) * 5;
    const yE = (e) => YC - (e / DMAX) * HALF;
    const xT = (t) => X0 + (Math.max(0, Math.min(dur, t)) / dur) * (X1 - X0);
    // dominio de la línea: apertura ±4; se reescala solo si la línea se sale
    const lv = hist.map((p) => p.l).filter((v) => typeof v === 'number');
    const hasLine = lv.length >= 2;
    let lc = hasLine ? lv[0] : 0, lr = esHb ? 2 : 4;
    if (hasLine) {
      const lmin = Math.min(...lv), lmax = Math.max(...lv);
      if (lmin < lc - lr || lmax > lc + lr) { lc = (lmin + lmax) / 2; lr = Math.max(lr, (lmax - lmin) / 2 + (esHb ? 0.5 : 1)); }
    }
    const yL = (l) => YC - ((l - lc) / lr) * HALF;
    const pts = hist.map((p) => ({ x: +xT(p.t).toFixed(1), y: +yE(p.e).toFixed(1), e: p.e, t: p.t, l: p.l }));
    const last = pts[pts.length - 1];
    const edgePath = smoothPath(pts);
    const areaPath = `${edgePath} L${last.x} ${YC} L${pts[0].x} ${YC} Z`;
    let linePath = '';
    if (hasLine) {
      const lp = pts.filter((p) => typeof p.l === 'number');
      linePath = `M${lp[0].x} ${yL(lp[0].l).toFixed(1)}`;
      for (let i = 1; i < lp.length; i++) {
        const y = yL(lp[i].l).toFixed(1);
        if (lp[i].l !== lp[i - 1].l) linePath += ` H${lp[i].x} V${y}`; else linePath += ` H${lp[i].x}`;
      }
      linePath += ` H${last.x}`;
    }
    const leftTicks = hasLine ? [lc + lr, lc + lr / 2, lc, lc - lr / 2, lc - lr].map((v) => ({ v: esHb ? f1(v) : String(Math.round(v * 2) / 2), y: yL(v) })) : [];
    const rightVals = esHb ? [DMAX, umbP, 0, umbN, -DMAX] : [DMAX, umbP, 0, umbN, -DMAX];
    const rightTicks = [...new Set(rightVals)].map((v) => ({ v: fs(v), y: yE(v) }));
    const grid = esHb ? [0, 30, 60] : [0, qLen, qLen * 2, qLen * 3, dur];
    const xLabels = esHb
      ? [[0, '1T'], [15, "15'"], [30, 'HT'], [45, "45'"], [60, 'FT']]
      : [[0, 'Q1'], [qLen, 'Q2'], [qLen * 2, 'Q3'], [qLen * 3, 'Q4'], [dur, 'FT']];
    return { pts, last, edgePath, areaPath, linePath, hasLine, leftTicks, rightTicks, grid: grid.map(xT), xLabels: xLabels.map(([t, l]) => [xT(t), l]), yUmbP: yE(umbP), yUmbN: yE(umbN), umbP, umbN, dur, qLen, xT, yE };
  }, [pick, esHb]);
}

export default function EdgeChart({ pick, esHb, lineaActual, proyeccion, edgeActual }) {
  const m = useChartModel(pick, esHb);
  const edgeNow = edgeActual ?? (m ? m.last.e : null);
  return (
    <div className="v3chart edge-chart">
      <div className="h"><div><h3 className="eyebrow">Edge + línea de la casa de apuestas</h3><p className="sub">Timing en tiempo real para decidir tu entrada</p></div></div>
      {!m ? (
        <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5, marginTop: 12 }}>El gráfico se dibuja con los primeros minutos de juego. Si el partido ya cerró, la serie en vivo no queda disponible.</p>
      ) : (
        <svg viewBox="0 0 330 178" role="img" aria-label="Edge y línea O/U por minuto">
          <defs>
            <linearGradient id="v3-fill" gradientUnits="userSpaceOnUse" x1="0" y1={Y0} x2="0" y2={Y1}>
              <stop offset="0" stopColor={OVER} stopOpacity=".16" /><stop offset=".5" stopColor={OVER} stopOpacity=".16" />
              <stop offset=".5" stopColor={UNDER} stopOpacity=".16" /><stop offset="1" stopColor={UNDER} stopOpacity=".16" />
            </linearGradient>
            <linearGradient id="v3-stroke" gradientUnits="userSpaceOnUse" x1="0" y1={Y0} x2="0" y2={Y1}>
              <stop offset="0" stopColor={OVER} /><stop offset=".5" stopColor={OVER} /><stop offset=".5" stopColor={UNDER} /><stop offset="1" stopColor={UNDER} />
            </linearGradient>
          </defs>
          {/* eje izquierdo: línea O/U · eje derecho: edge */}
          <g fontSize="9.5" fontWeight="700" fill={GOLD} textAnchor="end">{m.leftTicks.map((t, i) => <text key={i} x="26" y={(t.y + 3.5).toFixed(1)}>{t.v}</text>)}</g>
          <g fontSize="9.5" fontWeight="700" fill="#6E6E73" textAnchor="start">{m.rightTicks.map((t, i) => <text key={i} x="303" y={(t.y + 3.5).toFixed(1)}>{t.v}</text>)}</g>
          {/* cuadrícula por cuarto / mitad */}
          <g stroke="#E4E4E9" strokeWidth="1">{m.grid.map((x, i) => <line key={i} x1={x.toFixed(1)} y1={Y0} x2={x.toFixed(1)} y2={Y1} />)}<line x1={X0} y1={Y1} x2={X1} y2={Y1} /></g>
          {/* umbral real por deporte */}
          <line x1={X0} y1={m.yUmbP.toFixed(1)} x2={X1} y2={m.yUmbP.toFixed(1)} stroke={UMB} strokeWidth="1.2" strokeDasharray="4 4" />
          <line x1={X0} y1={m.yUmbN.toFixed(1)} x2={X1} y2={m.yUmbN.toFixed(1)} stroke={UMB} strokeWidth="1.2" strokeDasharray="4 4" />
          <text x={X0 + 3} y={(m.yUmbP - 3).toFixed(1)} fontSize="8" fontWeight="800" fill={UMB}>umbral {fs(m.umbP)}</text>
          <text x={X0 + 3} y={(m.yUmbN + 9).toFixed(1)} fontSize="8" fontWeight="800" fill={UMB}>umbral {fs(m.umbN)}</text>
          {/* cero */}
          <line x1={X0} y1={YC} x2={X1} y2={YC} stroke="#A1A1A6" strokeWidth="1" />
          {/* área + edge */}
          <path d={m.areaPath} fill="url(#v3-fill)" />
          <path d={m.edgePath} fill="none" stroke="url(#v3-stroke)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          {/* línea O/U escalonada */}
          {m.hasLine && <path d={m.linePath} fill="none" stroke={GOLD} strokeWidth="2.2" strokeLinejoin="round" />}
          {/* momento actual */}
          <line x1={m.last.x} y1="8" x2={m.last.x} y2={Y1} stroke="#DC2626" strokeWidth="1.4" />
          <rect x={(m.last.x - 18).toFixed(1)} y="0" width="36" height="14" rx="4" fill="#FBE3E3" />
          <text x={m.last.x} y="10.5" fontSize="9.5" fontWeight="800" fill="#B42121" textAnchor="middle">{fs(+f1(edgeNow))}</text>
          <circle cx={m.last.x} cy={m.last.y} r="3.5" fill={m.last.e >= 0 ? OVER : UNDER} stroke="#fff" strokeWidth="1.5" />
          {/* eje x */}
          <g fontSize="10" fontWeight="700" fill="#6E6E73" textAnchor="middle">{m.xLabels.map(([x, l], i) => <text key={i} x={x.toFixed(1)} y="168">{l}</text>)}</g>
        </svg>
      )}
      <div className="v3leg" style={{ marginTop: 10 }}>
        <span style={{ display: 'flex', gap: 12 }}>
          <span><i className="sq" style={{ background: OVER }} />OVER</span>
          <span><i className="sq" style={{ background: UNDER }} />UNDER</span>
          <span><i className="sq" style={{ background: GOLD }} />Línea O/U</span>
        </span>
        <span><i className="d" style={{ borderColor: UMB }} />{esHb ? 'umbral +3 / −2' : 'umbral ±10'}</span>
      </div>
      <div className="kp">
        <div><p className="l">Edge actual</p><p className={`v${edgeNow == null ? '' : edgeNow < 0 ? ' u' : ' g'}`}>{edgeNow == null ? '—' : fs(+f1(edgeNow))}</p></div>
        <div><p className="l">Línea O/U</p><p className="v">{lineaActual ?? (m && m.hasLine ? m.pts.filter((p) => typeof p.l === 'number').slice(-1)[0].l : '—')}</p></div>
        <div><p className="l">Proyección</p><p className="v o">{proyeccion != null ? (esHb ? f1(proyeccion) : Math.round(proyeccion)) : '—'}</p></div>
      </div>
    </div>
  );
}
